import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
  memo,
} from 'react';
import { cx } from '@trinity/chat-components/utils';
import { FocusableAnimation } from './FocusableAnimation';
import type { FocusableClasses } from './interfaces';
import styles from './style.module.css';

type Props<R extends FocusableRefObject> = {
  ref?: R;
  autoFocus?: boolean;
  children: React.ReactNode;
  className?: string;
  classes?: FocusableClasses;
  disabled?: boolean;
  onBlur?: () => unknown;
  onFocus?: () => unknown;
};

type FocusableRefObject = React.Ref<{
  blur: () => unknown;
  focus: () => unknown;
}>;

const Focusable = <R extends FocusableRefObject>({
  autoFocus = false,
  classes = {},
  disabled,
  onBlur,
  onFocus,
  ref,
  ...props
}: Props<R>) => {
  const [focused, setFocus] = useState(autoFocus);
  const focusRef = useRef<HTMLDivElement>(null);

  const handleBlur = useCallback(() => {
    setFocus(false);
    onBlur?.();
  }, [onBlur]);

  const handleFocus = useCallback(() => {
    if (disabled) return;
    setFocus(true);
    onFocus?.();
  }, [disabled, onFocus]);

  useImperativeHandle(ref, () => {
    return {
      blur: handleBlur,
      focus: handleFocus,
    };
  }, [handleBlur, handleFocus]);

  const classnames = useMemo(() => {
    return {
      root: cx(styles.root, classes.root, props.className),
    };
  }, [
    classes.root,
    props.className,
  ]);

  return (
    <div
      className={classnames.root}
      onBlur={handleBlur}
      onFocus={handleFocus}
      ref={focusRef}
      tabIndex={0}>
      {props.children}
      <FocusableAnimation
        classes={classes}
        focused={focused} />
    </div>
  );
};

const FocusableRef = memo(Focusable);

export { FocusableRef as Focusable };
export default FocusableRef;